import { useState, useEffect } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { KeyRound, MessageCircle, User, Building2, Phone, Mail, Lock, Eye, EyeOff, GraduationCap, Trophy, Search, ArrowLeft, Check, Users } from "lucide-react";
import logoGGEE from "../assets/logo-ggee.jpg";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Funções de formatação
const formatCPF = (value) => {
  const numbers = value.replace(/\D/g, '').slice(0, 11);
  return numbers
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const formatPhone = (value) => {
  const numbers = value.replace(/\D/g, '').slice(0, 11);
  if (numbers.length <= 10) {
    return numbers
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  return numbers
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2');
};

// Validação de username
const validateUsername = (value) => {
  const errors = [];
  
  if (value.length > 0 && value.length < 4) {
    errors.push("Mínimo 4 caracteres");
  }
  if (value.length > 20) {
    errors.push("Máximo 20 caracteres");
  }
  if (value.includes(' ')) {
    errors.push("Sem espaços");
  }
  if (value.length > 0 && !/^[a-zA-Z]/.test(value)) {
    errors.push("Deve começar com letra");
  }
  if (value.length > 0 && !/^[a-zA-Z][a-zA-Z0-9_.]*$/.test(value)) {
    errors.push("Apenas letras, números, _ e .");
  }
  
  return errors;
};

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Estado para mostrar login de aluno
  const [showAlunoLogin, setShowAlunoLogin] = useState(false);
  const [alunoStep, setAlunoStep] = useState("escola"); // escola, login, cadastro
  const [escolas, setEscolas] = useState([]);
  const [searchEscola, setSearchEscola] = useState("");
  const [selectedEscola, setSelectedEscola] = useState(null);
  const [alunoUsername, setAlunoUsername] = useState("");
  const [alunoPassword, setAlunoPassword] = useState("");
  
  // Estado para cadastro do aluno
  const [alunosDisponiveis, setAlunosDisponiveis] = useState([]);
  const [selectedAlunoId, setSelectedAlunoId] = useState("");
  const [novoAlunoUsername, setNovoAlunoUsername] = useState("");
  const [novoAlunoSenha, setNovoAlunoSenha] = useState("");
  const [novoAlunoConfirmSenha, setNovoAlunoConfirmSenha] = useState("");
  const [searchAluno, setSearchAluno] = useState("");
  
  
  // Campos de login
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  
  // Campos de cadastro
  const [registerData, setRegisterData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    nome_completo: "",
    cpf: "",
    telefone: "",
    email: "",
    nome_escola: ""
  });
  
  // Recuperação de senha
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotUsername, setForgotUsername] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [whatsappLink, setWhatsappLink] = useState("");
  const [generatedCode, setGeneratedCode] = useState("");
  
  // Importação de dados inicial
  const [showImportButton, setShowImportButton] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    // Verificar se o banco está vazio para mostrar botão de importação
    const checkDatabase = async () => {
      try {
        const response = await axios.get(`${API}/health`);
        // Se conseguiu conectar, verificar se há usuários tentando login
      } catch (error) {
        // Se der erro, pode ser que o banco esteja vazio
      }
    };
    checkDatabase();
    
    return () => {
      setShowForgotPassword(false);
    };
  }, []);

  // Buscar escolas disponíveis quando abrir login de aluno
  useEffect(() => {
    if (showAlunoLogin) {
      fetchEscolas();
    }
  }, [showAlunoLogin]);

  const fetchEscolas = async () => {
    try {
      const response = await axios.get(`${API}/arena/escolas`);
      setEscolas(response.data);
    } catch (error) {
      console.error("Erro ao buscar escolas");
    }
  };

  const filteredEscolas = escolas.filter(escola => 
    escola.nome.toLowerCase().includes(searchEscola.toLowerCase()) ||
    escola.codigo.toLowerCase().includes(searchEscola.toLowerCase())
  );

  const handleAlunoLogin = async () => {
    if (!selectedEscola || !alunoUsername || !alunoPassword) {
      toast.error("Preencha todos os campos");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/arena/aluno/login`, {
        username: alunoUsername,
        password: alunoPassword,
        escola_codigo: selectedEscola.codigo
      });
      
      const { token, aluno, escola } = response.data;
      
      // Salvar dados do aluno no localStorage
      localStorage.setItem('arena_aluno_token', token);
      localStorage.setItem('arena_aluno_data', JSON.stringify({ ...aluno, escola: escola.nome }));
      
      toast.success(`Bem-vindo, ${aluno.nome}!`);
      
      // Redirecionar para a página do aluno
      navigate('/aluno');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao fazer login");
    } finally {
      setLoading(false);
    }
  };

  const resetAlunoLogin = () => {
    setShowAlunoLogin(false);
    setAlunoStep("escola");
    setSelectedEscola(null);
    setAlunoUsername("");
    setAlunoPassword("");
    setSearchEscola("");
    setAlunosDisponiveis([]);
    setSelectedAlunoId("");
    setNovoAlunoUsername("");
    setNovoAlunoSenha("");
    setNovoAlunoConfirmSenha("");
    setSearchAluno("");
  };

  // Buscar alunos disponíveis para cadastro
  const fetchAlunosDisponiveis = async () => {
    if (!selectedEscola) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/arena/escola/${selectedEscola.codigo}/alunos-disponiveis`);
      setAlunosDisponiveis(response.data);
    } catch (error) {
      toast.error("Erro ao buscar alunos");
    } finally {
      setLoading(false);
    }
  };

  // Filtrar alunos disponíveis pela busca
  const filteredAlunos = alunosDisponiveis.filter(aluno =>
    aluno.nome.toLowerCase().includes(searchAluno.toLowerCase())
  );

  // Ir para tela de cadastro
  const irParaCadastro = () => {
    fetchAlunosDisponiveis();
    setAlunoStep("cadastro");
  };

  // Criar conta do aluno
  const handleAlunoCadastro = async () => {
    if (!selectedAlunoId) {
      toast.error("Selecione seu nome na lista");
      return;
    }
    if (!novoAlunoUsername || novoAlunoUsername.length < 3) {
      toast.error("O nome de usuário deve ter pelo menos 3 caracteres");
      return;
    }
    if (!novoAlunoSenha || novoAlunoSenha.length < 4) {
      toast.error("A senha deve ter pelo menos 4 caracteres");
      return;
    }
    if (novoAlunoSenha !== novoAlunoConfirmSenha) {
      toast.error("As senhas não coincidem");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/arena/aluno/auto-cadastro?escola_codigo=${selectedEscola.codigo}`, {
        aluno_id: selectedAlunoId,
        username: novoAlunoUsername.toLowerCase(),
        password: novoAlunoSenha
      });

      toast.success("Conta criada com sucesso! Faça login para continuar.");
      
      // Voltar para tela de login com username preenchido
      setAlunoUsername(novoAlunoUsername.toLowerCase());
      setAlunoPassword("");
      setAlunoStep("login");
      
      // Limpar campos de cadastro
      setSelectedAlunoId("");
      setNovoAlunoUsername("");
      setNovoAlunoSenha("");
      setNovoAlunoConfirmSenha("");
      setSearchAluno("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar conta");
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterChange = (field, value) => {
    let formattedValue = value;
    
    if (field === 'cpf') {
      formattedValue = formatCPF(value);
    } else if (field === 'telefone') {
      formattedValue = formatPhone(value);
    } else if (field === 'username') {
      // Converter para minúsculas e remover espaços
      formattedValue = value.toLowerCase().replace(/\s/g, '');
    }
    
    setRegisterData(prev => ({ ...prev, [field]: formattedValue }));
  };

  const validateRegisterForm = () => {
    const { username, password, confirmPassword, nome_completo, cpf, telefone, email, nome_escola } = registerData;
    
    // Validar username
    const usernameErrors = validateUsername(username);
    if (usernameErrors.length > 0) {
      toast.error(`Nome de usuário: ${usernameErrors[0]}`);
      return false;
    }
    
    if (!nome_completo || nome_completo.length < 3) {
      toast.error("Nome completo é obrigatório");
      return false;
    }
    
    const cpfNumbers = cpf.replace(/\D/g, '');
    if (cpfNumbers.length !== 11) {
      toast.error("CPF deve ter 11 dígitos");
      return false;
    }
    
    const phoneNumbers = telefone.replace(/\D/g, '');
    if (phoneNumbers.length < 10 || phoneNumbers.length > 11) {
      toast.error("Telefone inválido. Digite DDD + número");
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Email inválido");
      return false;
    }
    
    if (!nome_escola || nome_escola.length < 3) {
      toast.error("Nome da escola é obrigatório");
      return false;
    }
    
    if (password.length < 8) {
      toast.error("Senha deve ter no mínimo 8 caracteres");
      return false;
    }
    
    if (password !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return false;
    }
    
    return true;
  };

  // Função para importar dados iniciais
  const handleImportInitialData = async () => {
    setImporting(true);
    try {
      const response = await axios.post(`${API}/setup/import-initial-data`);
      toast.success(response.data.message || "Dados importados com sucesso!");
      setShowImportButton(false);
      // Recarregar a página para atualizar o estado
      window.location.reload();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Banco de dados já possui usuários. Faça login como admin.");
        setShowImportButton(false);
      } else {
        toast.error(error.response?.data?.detail || "Erro ao importar dados");
      }
    } finally {
      setImporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegistering) {
        if (!validateRegisterForm()) {
          setLoading(false);
          return;
        }
        
        const response = await axios.post(`${API}/auth/register`, {
          username: registerData.username,
          password: registerData.password,
          nome_completo: registerData.nome_completo,
          cpf: registerData.cpf,
          telefone: registerData.telefone,
          email: registerData.email,
          nome_escola: registerData.nome_escola
        });
        
        toast.success("Cadastro realizado com sucesso!");
        onLogin(response.data.token, response.data.username);
      } else {
        const response = await axios.post(`${API}/auth/login`, {
          username,
          password
        });
        
        toast.success("Login realizado com sucesso!");
        onLogin(response.data.token, response.data.username);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Erro ao processar solicitação";
      
      // Se o erro indicar que não há usuários, mostrar botão de importação
      if (errorMessage.toLowerCase().includes("usuário não encontrado") || 
          errorMessage.toLowerCase().includes("user not found")) {
        setShowImportButton(true);
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordStep1 = async () => {
    if (!forgotUsername) {
      toast.error("Digite seu nome de usuário");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        username: forgotUsername
      });

      setWhatsappLink(response.data.whatsapp_link);
      setGeneratedCode(response.data.code);
      setForgotStep(2);
      toast.success("Código gerado! Envie pelo WhatsApp ou anote o código.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao gerar código");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetCode || !newPassword || !confirmPassword) {
      toast.error("Preencha todos os campos");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("A senha deve ter no mínimo 8 caracteres");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        username: forgotUsername,
        code: resetCode,
        new_password: newPassword
      });

      toast.success("Senha redefinida com sucesso!");
      flushSync(() => {
        setShowForgotPassword(false);
        setForgotStep(1);
        setForgotUsername("");
        setResetCode("");
        setNewPassword("");
        setConfirmPassword("");
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao redefinir senha");
    } finally {
      setLoading(false);
    }
  };

  const resetRegisterForm = () => {
    setRegisterData({
      username: "",
      password: "",
      confirmPassword: "",
      nome_completo: "",
      cpf: "",
      telefone: "",
      email: "",
      nome_escola: ""
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)'
    }}>
      <Card className={`w-full shadow-2xl border-0 ${isRegistering ? 'max-w-lg' : 'max-w-md'}`} data-testid="login-card">
        <CardHeader className="space-y-3 text-center pb-6 pt-6">
          {/* Logo */}
          <div className="flex justify-center mb-2">
            <img 
              src={logoGGEE} 
              alt="GGEE Logo" 
              className="h-28 w-auto rounded-lg shadow-md"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk' }}>
            GGEE
          </CardTitle>
          <CardDescription className="text-sm text-gray-600">
            Gerenciador de Gestão de Escola Esportiva
          </CardDescription>
          <div className="pt-1">
            <Badge variant="outline" className="text-xs">
              {isRegistering ? "Criar nova conta" : "Acesse sua conta"}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="px-6 pb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isRegistering ? (
              // FORMULÁRIO DE LOGIN
              <>
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-sm font-semibold">Usuário</Label>
                  <Input
                    id="username"
                    data-testid="username-input"
                    type="text"
                    placeholder="Digite seu usuário"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    className="h-11 border-gray-300 focus:border-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-sm font-semibold">Senha</Label>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Esqueci minha senha
                    </button>
                  </div>
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    placeholder="Digite sua senha"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11 border-gray-300 focus:border-blue-500"
                  />
                </div>
                
                {/* Botão de Importação de Dados (só aparece quando banco está vazio) */}
                {showImportButton && (
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-sm text-purple-800 mb-3">
                      <strong>Banco de dados vazio detectado!</strong><br />
                      Clique abaixo para importar os dados salvos (usuários, alunos, turmas, etc.)
                    </p>
                    <Button
                      type="button"
                      onClick={handleImportInitialData}
                      disabled={importing}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      data-testid="import-initial-data-button"
                    >
                      {importing ? "Importando..." : "🗄️ Importar Dados do Backup"}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              // FORMULÁRIO DE CADASTRO
              <>
                {/* Dados Pessoais */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-gray-700 border-b pb-2">
                    <User className="w-4 h-4" />
                    <span className="text-sm font-semibold">Dados Pessoais</span>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="nome_completo" className="text-sm">Nome Completo *</Label>
                    <Input
                      id="nome_completo"
                      type="text"
                      placeholder="Seu nome completo"
                      value={registerData.nome_completo}
                      onChange={(e) => handleRegisterChange('nome_completo', e.target.value)}
                      required
                      className="h-10"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="cpf" className="text-sm">CPF *</Label>
                      <Input
                        id="cpf"
                        type="text"
                        placeholder="000.000.000-00"
                        value={registerData.cpf}
                        onChange={(e) => handleRegisterChange('cpf', e.target.value)}
                        required
                        className="h-10"
                        maxLength={14}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="telefone" className="text-sm flex items-center gap-1">
                        <Phone className="w-3 h-3" /> Telefone *
                      </Label>
                      <Input
                        id="telefone"
                        type="text"
                        placeholder="(00) 00000-0000"
                        value={registerData.telefone}
                        onChange={(e) => handleRegisterChange('telefone', e.target.value)}
                        required
                        className="h-10"
                        maxLength={15}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm flex items-center gap-1">
                      <Mail className="w-3 h-3" /> Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={registerData.email}
                      onChange={(e) => handleRegisterChange('email', e.target.value)}
                      required
                      className="h-10"
                    />
                  </div>
                </div>
                
                {/* Dados da Escola */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-2 text-gray-700 border-b pb-2">
                    <Building2 className="w-4 h-4" />
                    <span className="text-sm font-semibold">Dados da Escola</span>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="nome_escola" className="text-sm">Nome da Escola *</Label>
                    <Input
                      id="nome_escola"
                      type="text"
                      placeholder="Nome da sua escola esportiva"
                      value={registerData.nome_escola}
                      onChange={(e) => handleRegisterChange('nome_escola', e.target.value)}
                      required
                      className="h-10"
                    />
                  </div>
                </div>
                
                {/* Dados de Acesso */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-2 text-gray-700 border-b pb-2">
                    <Lock className="w-4 h-4" />
                    <span className="text-sm font-semibold">Dados de Acesso</span>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="reg_username" className="text-sm">Nome de Usuário *</Label>
                    <Input
                      id="reg_username"
                      type="text"
                      placeholder="ex: escola.futebol_sp"
                      value={registerData.username}
                      onChange={(e) => handleRegisterChange('username', e.target.value)}
                      required
                      className="h-10"
                      maxLength={20}
                    />
                    <p className="text-xs text-gray-500">
                      4-20 caracteres • Apenas letras, números, _ e . • Começa com letra
                    </p>
                    {registerData.username && validateUsername(registerData.username).length > 0 && (
                      <p className="text-xs text-red-500">
                        {validateUsername(registerData.username).join(' • ')}
                      </p>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="reg_password" className="text-sm">Senha *</Label>
                      <div className="relative">
                        <Input
                          id="reg_password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Mín. 8 caracteres"
                          value={registerData.password}
                          onChange={(e) => handleRegisterChange('password', e.target.value)}
                          required
                          className="h-10 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg_confirm_password" className="text-sm">Confirmar Senha *</Label>
                      <div className="relative">
                        <Input
                          id="reg_confirm_password"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Repita a senha"
                          value={registerData.confirmPassword}
                          onChange={(e) => handleRegisterChange('confirmPassword', e.target.value)}
                          required
                          className="h-10 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  {registerData.password && registerData.password.length < 8 && (
                    <p className="text-xs text-red-500">A senha deve ter no mínimo 8 caracteres</p>
                  )}
                  {registerData.confirmPassword && registerData.password !== registerData.confirmPassword && (
                    <p className="text-xs text-red-500">As senhas não coincidem</p>
                  )}
                </div>
              </>
            )}
            
            <Button
              type="submit"
              data-testid="submit-button"
              className="w-full h-11 bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-semibold shadow-lg"
              disabled={loading}
            >
              {loading ? "Carregando..." : (isRegistering ? "Criar Conta" : "Entrar no Sistema")}
            </Button>
          </form>
          
          <div className="mt-5 text-center border-t pt-5">
            <button
              data-testid="toggle-register-button"
              onClick={() => {
                setIsRegistering(!isRegistering);
                if (!isRegistering) {
                  resetRegisterForm();
                }
              }}
              className="text-sm text-gray-600 hover:text-gray-800 font-medium transition-colors"
            >
              {isRegistering ? "Já tem uma conta? Fazer login" : "Não tem conta? Criar nova conta"}
            </button>
          </div>
          
          {/* Botão Sou Aluno - Acesso à Arena */}
          {!isRegistering && (
            <div className="mt-4">
              <Button
                type="button"
                variant="outline"
                data-testid="sou-aluno-button"
                onClick={() => setShowAlunoLogin(true)}
                className="w-full h-11 border-purple-300 text-purple-700 hover:bg-purple-50 hover:border-purple-400 font-semibold"
              >
                <GraduationCap className="w-5 h-5 mr-2" />
                Sou Aluno - Acessar Arena
              </Button>
            </div>
          )}
          
          {/* Botão Sou Responsável - Acompanhar Filho */}
          {!isRegistering && (
            <div className="mt-3">
              <Button
                type="button"
                variant="outline"
                data-testid="sou-responsavel-button"
                onClick={() => window.location.href = '/responsavel'}
                className="w-full h-11 border-blue-300 text-blue-700 hover:bg-blue-50 hover:border-blue-400 font-semibold"
              >
                <Users className="w-5 h-5 mr-2" />
                Sou Responsável - Acompanhar Filho
              </Button>
            </div>
          )}
          
          {!isRegistering && (
            <>
              <div className="mt-4 text-center space-y-2">
                <p className="text-xs text-gray-500">Cada conta possui dados isolados e independentes</p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    💡 <strong>Precisa de um login apenas para chamada?</strong> Após fazer login, 
                    acesse <span className="font-semibold">Perfil</span> para criar logins auxiliares com permissões limitadas.
                  </p>
                </div>
              </div>
              
              {/* Contatos */}
              <div className="mt-5 pt-5 border-t space-y-2">
                <a
                  href="https://wa.me/5562996379383?text=Olá! Preciso de ajuda com o sistema GEE"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 text-green-600 hover:text-green-700 font-medium transition-colors text-sm"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  <span>(62) 99637-9383</span>
                </a>

                <a
                  href="https://www.instagram.com/ggeeapp?igsh=YWF1aGptaHZod3J3"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 text-pink-600 hover:text-pink-700 font-medium transition-colors text-sm"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  <span>@ggeeapp</span>
                </a>

                <p className="text-xs text-gray-500 text-center">Suporte e informações sobre planos</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Recuperação de Senha */}
      <Dialog key="forgot-password-dialog" open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5" />
              Recuperar Senha
            </DialogTitle>
            <DialogDescription>
              {forgotStep === 1 && "Digite seu nome de usuário para gerar o código de recuperação"}
              {forgotStep === 2 && "Use o código gerado para redefinir sua senha"}
            </DialogDescription>
          </DialogHeader>

          {forgotStep === 1 && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-username">Nome de Usuário</Label>
                <Input
                  id="forgot-username"
                  value={forgotUsername}
                  onChange={(e) => setForgotUsername(e.target.value)}
                  placeholder="Digite seu usuário"
                />
              </div>
            </div>
          )}

          {forgotStep === 2 && (
            <div className="space-y-4 py-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <p className="text-sm font-semibold text-blue-900">Seu código de recuperação:</p>
                <p className="text-2xl font-bold text-blue-600 text-center tracking-wider">{generatedCode}</p>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  Enviar via WhatsApp
                </a>
                <p className="text-xs text-gray-600 text-center">Código válido por 15 minutos</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reset-code">Código de Recuperação</Label>
                <Input
                  id="reset-code"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  placeholder="Digite o código"
                  maxLength={6}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">Nova Senha</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirmar Nova Senha</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Digite novamente"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowForgotPassword(false);
                setForgotStep(1);
                setForgotUsername("");
                setResetCode("");
                setNewPassword("");
                setConfirmPassword("");
              }}
            >
              Cancelar
            </Button>
            {forgotStep === 1 && (
              <Button onClick={handleForgotPasswordStep1} disabled={loading}>
                {loading ? "Gerando..." : "Gerar Código"}
              </Button>
            )}
            {forgotStep === 2 && (
              <Button onClick={handleResetPassword} disabled={loading}>
                {loading ? "Redefinindo..." : "Redefinir Senha"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Login do Aluno */}
      <Dialog open={showAlunoLogin} onOpenChange={(open) => !open && resetAlunoLogin()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-purple-600" />
              GGEE Arena - Login do Aluno
            </DialogTitle>
            <DialogDescription>
              {alunoStep === "escola" && "Selecione a sua escola para continuar"}
              {alunoStep === "login" && `Escola: ${selectedEscola?.nome}`}
            </DialogDescription>
          </DialogHeader>

          {alunoStep === "escola" && (
            <div className="space-y-4 py-4">
              {/* Campo de busca */}
              <div className="space-y-2">
                <Label>Buscar Escola</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={searchEscola}
                    onChange={(e) => setSearchEscola(e.target.value)}
                    placeholder="Digite o nome ou código da escola"
                    className="pl-10"
                    data-testid="busca-escola-input"
                  />
                </div>
              </div>
              
              {/* Lista de escolas */}
              <div className="max-h-64 overflow-y-auto border rounded-lg">
                {filteredEscolas.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    {escolas.length === 0 ? "Carregando escolas..." : "Nenhuma escola encontrada"}
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredEscolas.map((escola) => (
                      <div
                        key={escola.codigo}
                        onClick={() => {
                          setSelectedEscola(escola);
                          setAlunoStep("login");
                        }}
                        className="p-3 hover:bg-purple-50 cursor-pointer transition-colors flex justify-between items-center"
                        data-testid={`escola-item-${escola.codigo}`}
                      >
                        <div>
                          <p className="font-medium text-gray-800">{escola.nome}</p>
                          <p className="text-xs text-gray-500">Código: {escola.codigo}</p>
                        </div>
                        <GraduationCap className="w-5 h-5 text-purple-400" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <p className="text-xs text-gray-500 text-center">
                Não encontrou sua escola? Peça o código ao seu professor.
              </p>
            </div>
          )}

          {alunoStep === "login" && (
            <div className="space-y-4 py-4">
              {/* Botão voltar */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setAlunoStep("escola");
                  setSelectedEscola(null);
                }}
                className="mb-2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Voltar para seleção de escola
              </Button>

              {/* Escola selecionada */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold text-purple-800">{selectedEscola?.nome}</p>
                  <p className="text-xs text-purple-600">Código: {selectedEscola?.codigo}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="aluno-username">Usuário</Label>
                <Input
                  id="aluno-username"
                  value={alunoUsername}
                  onChange={(e) => setAlunoUsername(e.target.value)}
                  placeholder="Digite seu usuário"
                  data-testid="aluno-username-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="aluno-password">Senha</Label>
                <Input
                  id="aluno-password"
                  type="password"
                  value={alunoPassword}
                  onChange={(e) => setAlunoPassword(e.target.value)}
                  placeholder="Digite sua senha"
                  data-testid="aluno-password-input"
                />
              </div>

              <Button
                onClick={handleAlunoLogin}
                disabled={loading || !alunoUsername || !alunoPassword}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                data-testid="aluno-login-button"
              >
                {loading ? "Entrando..." : "Entrar na Arena"}
              </Button>

              {/* Separador */}
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-2 text-gray-500">ou</span>
                </div>
              </div>

              {/* Botão criar conta */}
              <Button
                variant="outline"
                onClick={irParaCadastro}
                className="w-full border-green-300 text-green-700 hover:bg-green-50"
                data-testid="criar-conta-aluno-button"
              >
                <User className="w-4 h-4 mr-2" />
                Criar minha conta
              </Button>
            </div>
          )}

          {alunoStep === "cadastro" && (
            <div className="space-y-4 py-4">
              {/* Botão voltar */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAlunoStep("login")}
                className="mb-2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Voltar para login
              </Button>

              {/* Escola selecionada */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold text-purple-800">{selectedEscola?.nome}</p>
                  <p className="text-xs text-purple-600">Criar conta na Arena</p>
                </div>
              </div>

              {/* Selecionar nome */}
              <div className="space-y-2">
                <Label>1. Encontre seu nome</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={searchAluno}
                    onChange={(e) => setSearchAluno(e.target.value)}
                    placeholder="Digite seu nome para buscar"
                    className="pl-10"
                    data-testid="busca-aluno-input"
                  />
                </div>
                
                <div className="max-h-40 overflow-y-auto border rounded-lg">
                  {loading ? (
                    <div className="p-4 text-center text-gray-500">Carregando...</div>
                  ) : filteredAlunos.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      {alunosDisponiveis.length === 0 
                        ? "Nenhum aluno disponível para cadastro" 
                        : "Nenhum aluno encontrado com esse nome"}
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredAlunos.map((aluno) => (
                        <div
                          key={aluno.id}
                          onClick={() => {
                            setSelectedAlunoId(aluno.id);
                            // Sugerir username baseado no nome
                            const nomeSugerido = aluno.nome.split(' ')[0].toLowerCase()
                              .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                            setNovoAlunoUsername(nomeSugerido);
                          }}
                          className={`p-3 cursor-pointer transition-colors flex justify-between items-center ${
                            selectedAlunoId === aluno.id 
                              ? 'bg-green-100 border-l-4 border-green-500' 
                              : 'hover:bg-gray-50'
                          }`}
                          data-testid={`aluno-item-${aluno.id}`}
                        >
                          <span className="font-medium">{aluno.nome}</span>
                          {selectedAlunoId === aluno.id && (
                            <Check className="w-4 h-4 text-green-600" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Campos de usuário e senha */}
              {selectedAlunoId && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="novo-username">2. Escolha um nome de usuário</Label>
                    <Input
                      id="novo-username"
                      value={novoAlunoUsername}
                      onChange={(e) => setNovoAlunoUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
                      placeholder="Ex: joao123"
                      data-testid="novo-aluno-username-input"
                    />
                    <p className="text-xs text-gray-500">Sem espaços, apenas letras minúsculas e números</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="novo-senha">3. Crie uma senha</Label>
                    <Input
                      id="novo-senha"
                      type="password"
                      value={novoAlunoSenha}
                      onChange={(e) => setNovoAlunoSenha(e.target.value)}
                      placeholder="Mínimo 4 caracteres"
                      data-testid="novo-aluno-senha-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirma-senha">4. Confirme a senha</Label>
                    <Input
                      id="confirma-senha"
                      type="password"
                      value={novoAlunoConfirmSenha}
                      onChange={(e) => setNovoAlunoConfirmSenha(e.target.value)}
                      placeholder="Digite a senha novamente"
                      data-testid="novo-aluno-confirma-senha-input"
                    />
                    {novoAlunoConfirmSenha && novoAlunoSenha !== novoAlunoConfirmSenha && (
                      <p className="text-xs text-red-500">As senhas não coincidem</p>
                    )}
                  </div>

                  <Button
                    onClick={handleAlunoCadastro}
                    disabled={loading || !novoAlunoUsername || !novoAlunoSenha || novoAlunoSenha !== novoAlunoConfirmSenha}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                    data-testid="criar-conta-button"
                  >
                    {loading ? "Criando conta..." : "Criar minha conta"}
                  </Button>
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={resetAlunoLogin}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
